/**
 * Contas e textos da carteira, sem estado: datas em pt-BR, validade, estado do
 * pacote, resumo e o fallback para o payload antigo (`service_credits` sem
 * `packages`). Usado pelos cartões, pelo resumo e pelo histórico.
 */
import { formatMoneyInput } from '../../../../../../utils/moneyUtils';

/** 1200.5 -> "R$ 1.200,50". */
export const formatBRL = (value) => `R$ ${formatMoneyInput(Number(value) || 0)}`;

const DAY_MS = 24 * 60 * 60 * 1000;
const ENDING_DAYS = 30;

const isEmptyDate = (value) => !value || String(value).startsWith('0000-00-00');

/** "2027-03-12" ou "2027-03-12 14:30:00" -> Date local (sem deslocamento de fuso). */
export function parseDate(value) {
  if (isEmptyDate(value)) return null;
  const [datePart, timePart = ''] = String(value).split(/[ T]/);
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  const [hours = 0, minutes = 0] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours || 0, minutes || 0);
}

const two = (n) => String(n).padStart(2, '0');

/** -> "12/03/2027" (ou "12/03" com withYear = false). Vazio quando não há data. */
export function formatDate(value, { withYear = true } = {}) {
  const date = parseDate(value);
  if (!date) return '';
  const dayMonth = `${two(date.getDate())}/${two(date.getMonth() + 1)}`;
  return withYear ? `${dayMonth}/${date.getFullYear()}` : dayMonth;
}

/** -> "12/03/2027 às 14:30". */
export function formatDateTime(value) {
  const date = parseDate(value);
  if (!date) return '';
  return `${formatDate(value)} às ${two(date.getHours())}:${two(date.getMinutes())}`;
}

/** Dias inteiros de hoje até a data (negativo quando já passou); null sem data. */
export function daysUntil(value) {
  const date = parseDate(value);
  if (!date) return null;
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((end - start) / DAY_MS);
}

export const sessionsLabel = (count) => (Number(count) === 1 ? '1 sessão' : `${count} sessões`);

/** Pílula de estado do pacote. */
export const PACKAGE_STATUS = {
  active: { label: 'Em uso', tone: 'active' },
  ending: { label: 'Acabando', tone: 'warning' },
  finished: { label: 'Concluído', tone: 'done' },
  expired: { label: 'Vencido', tone: 'muted' },
};

export const isOpenPackage = (pkg) => pkg.status === 'active' || pkg.status === 'ending';

/**
 * Texto e tom da validade: "Válido até 12/03/2027", "Vence em 12 dias" (âmbar
 * a 30 dias ou menos), "Vence hoje", "Vencido" ou "Não expira".
 */
export function expirationInfo(expirationDate, status) {
  if (isEmptyDate(expirationDate)) return { label: 'Não expira', tone: 'neutral', noExpiry: true };
  const days = daysUntil(expirationDate);
  if (status === 'expired' || days < 0) return { label: 'Vencido', tone: 'muted', noExpiry: false };
  if (status === 'finished') return { label: `Válido até ${formatDate(expirationDate)}`, tone: 'muted', noExpiry: false };
  if (days === 0) return { label: 'Vence hoje', tone: 'warning', noExpiry: false };
  if (days <= ENDING_DAYS) return { label: `Vence em ${days === 1 ? '1 dia' : `${days} dias`}`, tone: 'warning', noExpiry: false };
  return { label: `Válido até ${formatDate(expirationDate)}`, tone: 'neutral', noExpiry: false };
}

/** Estado calculado na tela quando o backend não manda `status`. */
export function deriveStatus(pkg) {
  const items = pkg.items || [];
  const remaining = items.reduce((sum, item) => sum + (Number(item.credits_remaining) || 0), 0);
  const days = daysUntil(pkg.expiration_date);
  if (days !== null && days < 0) return 'expired';
  if (remaining <= 0) return 'finished';
  const lowItem = items.some((item) => Number(item.credits_remaining) === 1);
  if (lowItem || (days !== null && days <= ENDING_DAYS)) return 'ending';
  return 'active';
}

/**
 * Payload antigo (só `service_credits`, linhas com saldo e dentro da validade):
 * um cartão "Créditos anteriores" por data de validade, uma linha por
 * procedimento, sem total (só "N sessões restantes").
 */
export function packagesFromLegacyCredits(serviceCredits) {
  const groups = {};
  (serviceCredits || []).forEach((credit) => {
    const expiration = isEmptyDate(credit.expiration_date) ? null : credit.expiration_date;
    const key = `legacy:${expiration || 'never'}`;
    if (!groups[key]) {
      groups[key] = {
        key,
        package_id: null,
        package_name: null,
        purchased_at: null,
        expiration_date: expiration,
        price: null,
        items: {},
      };
    }
    const procedureId = credit.procedure_id;
    if (!groups[key].items[procedureId]) {
      groups[key].items[procedureId] = {
        credit_id: credit.credit_id || `${key}:${procedureId}`,
        procedure_id: procedureId,
        procedure_name: credit.procedure_name,
        credits_total: null,
        credits_remaining: 0,
        unit_value: null,
      };
    }
    groups[key].items[procedureId].credits_remaining += Number(credit.credits_remaining) || 0;
  });

  return Object.values(groups).map((group) => {
    const items = Object.values(group.items)
      .sort((a, b) => String(a.procedure_name).localeCompare(String(b.procedure_name), 'pt-BR'));
    const pkg = { ...group, items };
    return { ...pkg, status: deriveStatus(pkg) };
  });
}

/** `packages` do contrato quando vier; senão o fallback. Sempre com `status`. */
export function normalizePackages(wallet) {
  if (!wallet) return [];
  if (Array.isArray(wallet.packages)) {
    return wallet.packages.map((pkg) => ({
      ...pkg,
      items: pkg.items || [],
      status: PACKAGE_STATUS[pkg.status] ? pkg.status : deriveStatus(pkg),
    }));
  }
  return packagesFromLegacyCredits(wallet.service_credits);
}

const expirationOrder = (pkg) => {
  const date = parseDate(pkg.expiration_date);
  return date ? date.getTime() : Number.MAX_SAFE_INTEGER;
};

/** Em uso primeiro, vencendo antes; depois, os mais recentes. */
export function sortPackages(packages) {
  return [...packages].sort((a, b) => {
    const openA = isOpenPackage(a);
    const openB = isOpenPackage(b);
    if (openA !== openB) return openA ? -1 : 1;
    if (openA) {
      const byExpiration = expirationOrder(a) - expirationOrder(b);
      if (byExpiration !== 0) return byExpiration;
    }
    const purchasedA = parseDate(a.purchased_at)?.getTime() || 0;
    const purchasedB = parseDate(b.purchased_at)?.getTime() || 0;
    return purchasedB - purchasedA;
  });
}

/** Números da faixa de resumo, só dos pacotes em uso. */
export function summarize(packages) {
  const open = packages.filter(isOpenPackage);
  const sessions = open.reduce(
    (sum, pkg) => sum + pkg.items.reduce((inner, item) => inner + (Number(item.credits_remaining) || 0), 0),
    0,
  );
  const nextExpiration = open
    .map((pkg) => pkg.expiration_date)
    .filter((date) => !isEmptyDate(date))
    .sort((a, b) => parseDate(a) - parseDate(b))[0] || null;
  return { sessions, activeCount: open.length, nextExpiration };
}

/** Histórico: ícone, sinal e quantidade de cada movimento. */
export function describeTransaction(transaction) {
  const type = String(transaction.type || '').toUpperCase();
  const credits = transaction.service_credits === null || transaction.service_credits === undefined
    ? null
    : Number(transaction.service_credits);
  const hasCredits = credits !== null && !Number.isNaN(credits) && credits !== 0;
  const amount = Number(transaction.amount) || 0;
  const isReturn = type === 'ADD' && !!transaction.appointment_id;

  let kind = 'other';
  let sign = '';
  if (type === 'ADD') { kind = isReturn ? 'returned' : 'purchase'; sign = '+'; }
  else if (type === 'DEDUCT') { kind = 'used'; sign = '-'; }
  else if (type === 'EXPIRE') { kind = 'expired'; sign = '-'; }
  else if (type === 'TRANSFER') { kind = 'transfer'; sign = ''; }

  let quantity = '';
  if (hasCredits) quantity = `${sign}${sessionsLabel(Math.abs(credits))}`;
  else if (amount > 0) quantity = `${sign}${formatBRL(amount)}`;

  return { kind, quantity, negative: sign === '-' };
}
